import React, { useCallback, useEffect, useState } from 'react';
import { Card, Tag, Typography, Tooltip, Table, Button, Popconfirm, message, Collapse, Switch, Space } from 'antd';
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  WarningOutlined,
  PlusOutlined,
  MinusOutlined,
  InboxOutlined,
  FileTextOutlined,
  TeamOutlined,
} from '@ant-design/icons';
import { Link } from 'react-router-dom';
import moment from 'moment-timezone';
import { useLazyQuery, useMutation } from 'react-apollo';

import { formatCurrency, formatWholeNumber } from '../../../../core/utils/campaigns';
import { useCampaignRequestorContext } from '../../../../core/components/campaignRequestor/useCampaignRequestorContext';
import CampaignOrderQueueFilters from './CampaignOrderQueueFilters';
import GET_CAMPAIGN_ORDERS_DRAFT from '../../../../core/GraphQl/Queries/GET_CAMPAIGN_ORDERS_DRAFT';
import { formatCampaignName } from '../../../../core/utils/campaignNaming';
import { useTimezone } from '../../../../core/hooks/useTimezone';
import ARCHIVE_CAMPAIGN_ORDER from '../../../../core/GraphQl/Mutations/ARCHIVE_CAMPAIGN_ORDER';
import { createEnhancedSearchFilter } from '../../../../core/utils/enhancedSearch';

const { Text } = Typography;

export const CampaignOrderDraft = () => {
  const { currentUserOrg } = useCampaignRequestorContext();
  const { timezone } = useTimezone();

  const [campaignOrders, setCampaignOrders] = useState([]);
  const [filteredOrgId, setFilteredOrgId] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedRowKeys, setExpandedRowKeys] = useState([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0,
  });
  const [archivingIds, setArchivingIds] = useState(new Set());
  const [filterMediaPlanStrategy, setFilterMediaPlanStrategy] = useState(false);

  // QUERIES
  const [
    fetchCampaignOrdersDraft,
    { data: dataCampaignOrdersDraft, loading: loadingCampaignOrdersDraft },
  ] = useLazyQuery(GET_CAMPAIGN_ORDERS_DRAFT, {
    fetchPolicy: 'no-cache',
  });

  // MUTATIONS
  const [archiveCampaignOrder, { loading: loadingArchive }] =
    useMutation(ARCHIVE_CAMPAIGN_ORDER);

  // Handle data when it comes back from the query
  useEffect(() => {
    if (dataCampaignOrdersDraft && dataCampaignOrdersDraft.campaignOrdersConnection) {
      setCampaignOrders(
        dataCampaignOrdersDraft.campaignOrdersConnection.edges.map((edge) => edge.node)
      );
      setPagination((prev) => ({
        ...prev,
        total: dataCampaignOrdersDraft.full.aggregate.count,
      }));
    }
  }, [dataCampaignOrdersDraft]);

  const handleFetchCampaignOrdersQueue = useCallback(
    ({
      skip = 0,
      first = pagination.pageSize,
      name = searchTerm,
      orgId = filteredOrgId,
      sortField,
      sortOrder,
      hasMediaPlanStrategy = filterMediaPlanStrategy,
    }) => {
      const where = {
        AND: [
          {
            status_in: ['DRAFT'],
            ...(orgId && {
              orgs_some: { OR: [{ id: orgId }, { parentOrg_some: { id: orgId } }] },
            }),
          },
        ],
      };

      const enhancedSearchFilter = createEnhancedSearchFilter(name);
      if (enhancedSearchFilter) {
        where['AND'].push(enhancedSearchFilter);
      }

      // Filter by Has Media Plan Strategy
      if (hasMediaPlanStrategy) {
        where['AND'].push({
          campaigns_some: {
            hasMediaPlanStrategy: true,
          },
        });
      }

      fetchCampaignOrdersDraft({
        variables: {
          where,
          skip,
          first,
          orderBy:
            sortField && sortOrder ? `${sortField}_${sortOrder.toUpperCase()}` : 'createdAt_DESC',
        },
      });
    },
    [fetchCampaignOrdersDraft, pagination.pageSize, filteredOrgId, searchTerm, filterMediaPlanStrategy]
  );

  const handleFilterChanged = useCallback(
    ({ first, name, orgId }) => {
      if (name !== undefined) {
        setSearchTerm(name);
      }
      if (orgId !== undefined) {
        setFilteredOrgId(orgId);
      }
      setPagination((prev) => ({ ...prev, current: 1 })); // Reset to first page
      handleFetchCampaignOrdersQueue({
        skip: 0,
        first: first || pagination.pageSize,
        name: name !== undefined ? name : searchTerm,
        orgId: orgId !== undefined ? orgId : filteredOrgId,
      });
    },
    [pagination.pageSize, handleFetchCampaignOrdersQueue, searchTerm, filteredOrgId]
  );

  const handleTableChange = (pagination, filters, sorter) => {
    const { current, pageSize } = pagination;
    const sortField = sorter.field;
    const sortOrder =
      sorter.order === 'ascend' ? 'asc' : sorter.order === 'descend' ? 'desc' : null;

    setPagination((prev) => ({ ...prev, current, pageSize }));
    handleFetchCampaignOrdersQueue({
      skip: (current - 1) * pageSize,
      first: pageSize,
      sortField,
      sortOrder,
      hasMediaPlanStrategy: filterMediaPlanStrategy,
    });
  };

  const handleMediaPlanStrategyFilterChange = (checked) => {
    setFilterMediaPlanStrategy(checked);
    handleFetchCampaignOrdersQueue({
      skip: 0,
      first: pagination.pageSize,
      hasMediaPlanStrategy: checked,
    });
  };

  const handleArchiveCampaignOrder = async (record) => {
    // Add the record ID to the archiving set
    setArchivingIds((prev) => {
      const next = new Set(prev);
      next.add(record.id);
      return next;
    });

    try {
      await archiveCampaignOrder({
        variables: {
          id: record.id,
        },
      });

      message.success('Campaign order archived successfully');

      // Refresh the data
      handleFetchCampaignOrdersQueue({
        skip: (pagination.current - 1) * pagination.pageSize,
        first: pagination.pageSize,
        name: searchTerm,
        orgId: filteredOrgId,
      });
    } catch (error) {
      message.error('Failed to archive campaign order: ' + error.message);
    } finally {
      // Remove the record ID from the archiving set
      setArchivingIds((prev) => {
        const next = new Set(prev);
        next.delete(record.id);
        return next;
      });
    }
  };

  useEffect(() => {
    if (currentUserOrg) {
      const userData = localStorage.getItem('userData');
      const orgId = userData ? JSON.parse(userData).role.org.id : null;
      const currentOrgId = orgId ?? currentUserOrg.id;

      setFilteredOrgId(currentOrgId);
      handleFetchCampaignOrdersQueue({
        skip: 0,
        first: pagination.pageSize,
        orgId: currentOrgId,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUserOrg]);

  // Helper function to extract and flatten all line items from all campaigns
  const getAllLineItems = (record) => {
    const allLineItems = [];
    if (record.campaigns && record.campaigns.length > 0) {
      record.campaigns.forEach((campaign) => {
        if (campaign.lineItems && campaign.lineItems.length > 0) {
          campaign.lineItems.forEach((lineItem) => {
            allLineItems.push({
              ...lineItem,
              campaignName: campaign.name,
              campaignId: campaign.id,
            });
          });
        }
      });
    }
    return allLineItems;
  };

  // Comprehensive validation function for line items
  const validateLineItem = (lineItem, campaignOrderGeoTargets = []) => {
    const validation = {
      isValid: true,
      missingRequirements: [],
      warnings: [],
    };

    // 1. Budget validation
    if (!lineItem.budgetTotal || lineItem.budgetTotal <= 0) {
      validation.isValid = false;
      validation.missingRequirements.push('Budget');
    } else if (lineItem.budgetTotal < 100) {
      validation.warnings.push('Budget is very low (< $100)');
    }

    // 2. Date validation
    const startDate = lineItem.startDate ? new Date(lineItem.startDate) : null;
    const endDate = lineItem.endDate ? new Date(lineItem.endDate) : null;
    const now = new Date();

    if (!startDate || !endDate) {
      validation.isValid = false;
      validation.missingRequirements.push('Start/End Date');
    } else {
      if (startDate >= endDate) {
        validation.isValid = false;
        validation.missingRequirements.push('Invalid Date Range');
      }
      if (endDate < now) {
        validation.warnings.push('Campaign ended');
      }
    }

    // 3. Targeting validation
    const hasTargeting = !!(
      (lineItem.targetingLocations && lineItem.targetingLocations.length > 0) ||
      (lineItem.targetingRegions && lineItem.targetingRegions.length > 0) ||
      (lineItem.targetingCountries && lineItem.targetingCountries.length > 0) ||
      (campaignOrderGeoTargets && campaignOrderGeoTargets.length > 0)
    );

    if (!hasTargeting) {
      validation.isValid = false;
      validation.missingRequirements.push('Targeting');
    }

    // 4. Audience validation
    const hasAudience = !!(
      (lineItem.firstPartyDataAudiences && lineItem.firstPartyDataAudiences.length > 0) ||
      lineItem.audienceType
    );

    if (!hasAudience) {
      validation.isValid = false;
      validation.missingRequirements.push('Audience');
    }

    // 5. Ad Group and Creative validation
    const hasAdGroup = !!(lineItem.adGroup && lineItem.adGroup.id);
    const hasCreatives = !!(
      lineItem.adGroup &&
      lineItem.adGroup.adGroupCreatives &&
      lineItem.adGroup.adGroupCreatives.length > 0
    );

    if (!hasAdGroup) {
      validation.isValid = false;
      validation.missingRequirements.push('Ad Group');
    } else if (!hasCreatives) {
      validation.isValid = false;
      validation.missingRequirements.push('Creatives');
    }

    return validation;
  };

  // Helper function to extract unique collaborators from campaign order
  const getCollaborators = (record) => {
    const collaborators = new Map();

    // Add creator
    if (record.createdBy) {
      collaborators.set(record.createdBy.id, {
        id: record.createdBy.id,
        name: record.createdBy.name || record.createdBy.email,
        email: record.createdBy.email,
        role: 'Creator',
        lastActivity: record.createdAt,
      });
    }

    // Add users from change log
    if (record.changeLog && record.changeLog.length > 0) {
      record.changeLog.forEach((change) => {
        if (change.changedBy) {
          const existing = collaborators.get(change.changedBy.id);
          if (!existing || new Date(change.createdAt) > new Date(existing.lastActivity)) {
            collaborators.set(change.changedBy.id, {
              id: change.changedBy.id,
              name: change.changedBy.name || change.changedBy.email,
              email: change.changedBy.email,
              role: existing ? existing.role : 'Contributor',
              lastActivity: change.createdAt,
            });
          }
        }
      });
    }

    // Add users from campaign history
    if (record.histories && record.histories.length > 0) {
      record.histories.forEach((history) => {
        if (history.author) {
          const existing = collaborators.get(history.author.id);
          if (!existing || new Date(history.createdAt) > new Date(existing.lastActivity)) {
            collaborators.set(history.author.id, {
              id: history.author.id,
              name: history.author.name || history.author.email,
              email: history.author.email,
              role: existing ? existing.role : 'Contributor',
              lastActivity: history.createdAt,
            });
          }
        }
      });
    }

    // Add users from audit history
    if (record.auditHistory && record.auditHistory.length > 0) {
      record.auditHistory.forEach((audit) => {
        if (audit.user) {
          const existing = collaborators.get(audit.user);
          if (!existing || new Date(audit.createdAt) > new Date(existing.lastActivity)) {
            collaborators.set(audit.user, {
              id: audit.user,
              name: audit.user, // Audit history might only have user string
              email: audit.user,
              role: existing ? existing.role : 'Contributor',
              lastActivity: audit.createdAt,
            });
          }
        }
      });
    }

    return Array.from(collaborators.values()).sort(
      (a, b) => new Date(b.lastActivity) - new Date(a.lastActivity)
    );
  };

  // Audience label helper for line items
  const getAudienceLabel = (lineItem) => {
    if (
      (lineItem.firstPartyDataAudiences && lineItem.firstPartyDataAudiences.length > 0) ||
      lineItem.audienceType === 'FIRST_PARTY_DATA'
    ) {
      return 'First Party Data';
    }
    if (lineItem.audienceType === 'ALL_USERS') {
      return 'All users in targeting area aged 21+';
    }
    if (lineItem.audienceType === 'THIRD_PARTY_DATA') {
      return 'Third Party Data';
    }
    return 'Not set';
  };

  // Targeting label helper for line items
  const getTargetingLabel = (lineItem) => {
    const hasLocations = lineItem.targetingLocations && lineItem.targetingLocations.length > 0;
    const hasRegions = lineItem.targetingRegions && lineItem.targetingRegions.length > 0;
    const hasCountries = lineItem.targetingCountries && lineItem.targetingCountries.length > 0;

    if (hasRegions) return 'Regions';
    if (hasLocations) return 'Local Targeting';
    if (hasCountries) return 'Country';
    return 'Not set';
  };

  const columns = [
    {
      title: 'Campaign Details',
      dataIndex: 'name',
      key: 'name',
      width: 300,
      render: (_, record) => (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            minWidth: 0,
          }}
        >
          <div>
            <Tooltip title={formatCampaignName(record, timezone)}>
              <Link
                to={`/campaigns/v2/orderqueue/details/${record.id}`}
                style={{
                  display: 'block',
                  maxWidth: '100%',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {formatCampaignName(record, timezone)}
              </Link>
            </Tooltip>
          </div>
          <div style={{ marginTop: '4px' }}>
            <Tag color="blue">{(record.advertiser && record.advertiser.name) || ''}</Tag>
          </div>
          <div style={{ marginTop: '4px', fontSize: '12px', color: '#8c8c8c' }}>
            Created: {record.createdAt ? moment(record.createdAt).format('MM/DD/YYYY') : 'N/A'}
          </div>
        </div>
      ),
    },
    {
      title: 'Start Date',
      dataIndex: 'startDate',
      key: 'startDate',
      render: (startDate) => moment(startDate).tz(timezone).format('MM/DD/YYYY'),
      sorter: true,
    },
    {
      title: 'End Date',
      dataIndex: 'endDate',
      key: 'endDate',
      render: (endDate) => moment(endDate).tz(timezone).format('MM/DD/YYYY'),
      sorter: true,
    },
    {
      title: 'Budget',
      dataIndex: 'budget',
      key: 'budget',
      render: (budget, record) => {
        const { cpm } = record;
        let impressionGoal = 0;
        if (cpm && budget && budget > 0 && cpm > 0) {
          impressionGoal = (budget / cpm) * 1000;
        }
        return (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              lineHeight: 1.2,
            }}
          >
            <div>Budget: {budget ? formatCurrency(budget) : 'N/A'}</div>
            <div>CPM: {cpm ? formatCurrency(cpm) : 'N/A'}</div>
            <div>Goal: {impressionGoal > 0 ? formatWholeNumber(impressionGoal) : 'N/A'}</div>
          </div>
        );
      },
      sorter: true,
    },
    {
      title: 'Created by',
      dataIndex: 'createdBy',
      key: 'createdBy',
      render: (createdBy) => createdBy?.name || createdBy?.email || '—',
    },
    {
      title: 'Media Plan',
      key: 'hasMediaPlanStrategy',
      render: (_, record) => {
        const hasStrategy = record.campaigns?.some(campaign => campaign.hasMediaPlanStrategy);
        return hasStrategy ? (
          <Tag color="purple">Strategy</Tag>
        ) : null;
      },
    },
    // TODO: Comment for the meantime revisit the UI
    // {
    //   title: 'Collaborators',
    //   key: 'collaborators',
    //   width: 180,
    //   align: 'center',
    //   render: (_, record) => {
    //     const collaborators = getCollaborators(record);

    //     if (collaborators.length === 0) {
    //       return (
    //         <div style={{ textAlign: 'center' }}>
    //           <div style={{ fontSize: '12px', color: '#8c8c8c' }}>No Collaborators</div>
    //           <TeamOutlined style={{ color: '#d9d9d9', fontSize: '16px' }} />
    //         </div>
    //       );
    //     }

    //     const displayCount = collaborators.length;
    //     const creator = collaborators.find(c => c.role === 'Creator');
    //     const contributors = collaborators.filter(c => c.role === 'Contributor');

    //     return (
    //       <Tooltip
    //         title={
    //           <div style={{ maxWidth: '300px' }}>
    //             <div style={{ fontWeight: 'bold', marginBottom: '8px' }}>Campaign Collaborators:</div>
    //             {creator && (
    //               <div style={{ marginBottom: '4px' }}>
    //                 <Text strong style={{ color: '#1890ff' }}>Creator:</Text> {creator.name}
    //               </div>
    //             )}
    //             {contributors.length > 0 && (
    //               <div>
    //                 <Text strong style={{ color: '#52c41a' }}>Contributors:</Text>
    //                 {contributors.map((contributor, index) => (
    //                   <div key={contributor.id} style={{ marginLeft: '8px', fontSize: '12px' }}>
    //                     • {contributor.name}
    //                   </div>
    //                 ))}
    //               </div>
    //             )}
    //           </div>
    //         }
    //         placement="left"
    //       >
    //         <div style={{ textAlign: 'center', cursor: 'pointer' }}>
    //           <div style={{ fontSize: '12px', color: '#1890ff', marginBottom: '4px' }}>
    //             {displayCount} {displayCount === 1 ? 'Collaborator' : 'Collaborators'}
    //           </div>
    //           <TeamOutlined style={{ color: '#1890ff', fontSize: '16px' }} />
    //         </div>
    //       </Tooltip>
    //     );
    //   },
    // },
    {
      title: 'Attribution',
      key: 'attribution',
      align: 'center',
      render: (_, record) => {
        const hasRegularAttribution = !!(
          (record.tags && record.tags.length > 0) ||
          (record.attributionTargets && record.attributionTargets.length > 0) ||
          (record.transactionsConfig && record.transactionsConfig.tag) ||
          (record.transactionsConfig && record.transactionsConfig.gtmAccess) ||
          (record.transactionsConfig && record.transactionsConfig.appId)
        );
        const isAwarenessCampaign = !!record.awarenessCampaign;
        const hasAttribution = hasRegularAttribution || isAwarenessCampaign;
        let attributionLabel = 'No Tag';
        if (isAwarenessCampaign) {
          attributionLabel = 'Awareness';
        } else if (hasRegularAttribution) {
          attributionLabel = 'Tagged';
        }

        return (
          <div style={{ textAlign: 'center' }}>
            <div>
              {hasAttribution ? (
                <CheckCircleOutlined style={{ color: '#52c41a', fontSize: '18px' }} />
              ) : (
                <CloseCircleOutlined style={{ color: '#ff4d4f', fontSize: '18px' }} />
              )}
            </div>
            <div style={{ fontSize: '12px', color: '#8c8c8c' }}>{attributionLabel}</div>
          </div>
        );
      },
    },
    {
      title: 'Line Items',
      key: 'lineItems',
      width: 150,
      align: 'center',
      render: (_, record) => {
        const allLineItems = getAllLineItems(record);
        const totalCount = allLineItems.length;
        let validCount = 0;
        let invalidCount = 0;
        let warningCount = 0;

        allLineItems.forEach((lineItem) => {
          const validation = validateLineItem(lineItem, record.geoTargets || []);
          if (validation.isValid) {
            validCount++;
            if (validation.warnings.length > 0) {
              warningCount++;
            }
          } else {
            invalidCount++;
          }
        });

        if (totalCount === 0) {
          return (
            <div>
              <div style={{ fontSize: '11px', fontWeight: 'bold' }}>No Line Items</div>
              <CloseCircleOutlined style={{ color: '#ff4d4f', fontSize: '12px' }} />
            </div>
          );
        }

        return (
          <div>
            <div style={{ fontSize: '11px', fontWeight: 'bold' }}>{totalCount} Line Items</div>
            <div style={{ fontSize: '10px', color: '#8c8c8c' }}>
              <CheckCircleOutlined style={{ color: '#52c41a', fontSize: '12px' }} /> Valid:{' '}
              {validCount}
            </div>
            <div style={{ fontSize: '10px', color: '#8c8c8c' }}>
              <CloseCircleOutlined style={{ color: '#ff4d4f', fontSize: '12px' }} /> Invalid:{' '}
              {invalidCount}
            </div>
            {warningCount > 0 && (
              <div style={{ fontSize: '10px', color: '#8c8c8c' }}>
                <WarningOutlined style={{ color: '#faad14', fontSize: '12px' }} /> Warnings:{' '}
                {warningCount}
              </div>
            )}
          </div>
        );
      },
    },
    {
      title: 'Actions',
      key: 'actions',
      align: 'center',
      width: 120,
      render: (_, record) => (
        <Popconfirm
          title="Are you sure you want to archive this campaign order?"
          description="This action cannot be undone. The campaign order will be moved to archived status."
          onConfirm={() => handleArchiveCampaignOrder(record)}
          okText="Yes, Archive"
          cancelText="Cancel"
          okType="danger"
        >
          <Button
            type="text"
            size="small"
            icon={<InboxOutlined />}
            loading={archivingIds.has(record.id)}
            danger
          />
        </Popconfirm>
      ),
    },
  ];

  const lineItemColumns = [
    {
      title: 'Line Item Details',
      dataIndex: 'name',
      key: 'name',
      width: 300,
      render: (name, lineItem) => (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            minWidth: 0,
          }}
        >
          <div>
            <Text style={{ fontSize: '13px', fontWeight: 'bold' }}>{name}</Text>
          </div>
          <div style={{ marginTop: '4px' }}>
            <Tag color="geekblue" size="small">
              {lineItem.campaignName}
            </Tag>
          </div>
        </div>
      ),
    },
    {
      title: 'Start Date',
      dataIndex: 'startDate',
      key: 'startDate',
      render: (startDate) => (
        <div style={{ display: 'flex', alignItems: 'center' }}>
          {startDate ? moment(startDate).tz(timezone).format('MM/DD/YYYY') : 'Not Set'}
        </div>
      ),
    },
    {
      title: 'End Date',
      dataIndex: 'endDate',
      key: 'endDate',
      render: (endDate) => (
        <div style={{ display: 'flex', alignItems: 'center' }}>
          {endDate ? moment(endDate).tz(timezone).format('MM/DD/YYYY') : 'Not Set'}
        </div>
      ),
    },
    {
      title: 'Budget',
      key: 'budget',
      render: (_, lineItem) => {
        const budget = lineItem.budgetTotal || 0;
        const cpm = lineItem.cpm || 0;
        const impressionGoal = budget && cpm && cpm > 0 ? (budget / cpm) * 1000 : 0;

        return (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              lineHeight: 1.2,
            }}
          >
            <div>Budget: {budget > 0 ? formatCurrency(budget) : 'N/A'}</div>
            <div>CPM: {cpm > 0 ? formatCurrency(cpm) : 'N/A'}</div>
            <div>Goal: {impressionGoal > 0 ? formatWholeNumber(impressionGoal) : 'N/A'}</div>
          </div>
        );
      },
    },
    {
      title: 'Created by',
      key: 'createdBy',
      render: (_, lineItem) => (
        <div style={{ display: 'flex', alignItems: 'center' }}>
          {lineItem.createdBy?.name || lineItem.createdBy?.email || '—'}
        </div>
      ),
    },
    {
      title: 'Targeting',
      key: 'targeting',
      align: 'center',
      render: (_, lineItem) => {
        const validation = validateLineItem(lineItem, lineItem.geoTargets || []);
        return (
          <div style={{ textAlign: 'center' }}>
            <div>
              {validation.missingRequirements.includes('Targeting') ? (
                <CloseCircleOutlined style={{ color: '#ff4d4f', fontSize: '18px' }} />
              ) : (
                <CheckCircleOutlined style={{ color: '#52c41a', fontSize: '18px' }} />
              )}
            </div>
            <div style={{ fontSize: '12px', color: '#8c8c8c' }}>{getTargetingLabel(lineItem)}</div>
          </div>
        );
      },
    },
    {
      title: 'Audience',
      key: 'audience',
      align: 'center',
      render: (_, lineItem) => {
        const validation = validateLineItem(lineItem, lineItem.geoTargets || []);
        return (
          <div style={{ textAlign: 'center' }}>
            <div>
              {validation.missingRequirements.includes('Audience') ? (
                <CloseCircleOutlined style={{ color: '#ff4d4f', fontSize: '18px' }} />
              ) : (
                <CheckCircleOutlined style={{ color: '#52c41a', fontSize: '18px' }} />
              )}
            </div>
            <div style={{ fontSize: '12px', color: '#8c8c8c' }}>{getAudienceLabel(lineItem)}</div>
          </div>
        );
      },
    },
    {
      title: 'Ad Group',
      key: 'adGroup',
      align: 'center',
      render: (_, lineItem) => {
        const validation = validateLineItem(lineItem, lineItem.geoTargets || []);
        const creativeCount = lineItem.adGroup?.adGroupCreatives?.length || 0;

        return (
          <div style={{ textAlign: 'center' }}>
            <div>
              {validation.missingRequirements.includes('Ad Group') ||
              validation.missingRequirements.includes('Creatives') ? (
                <CloseCircleOutlined style={{ color: '#ff4d4f', fontSize: '18px' }} />
              ) : (
                <CheckCircleOutlined style={{ color: '#52c41a', fontSize: '18px' }} />
              )}
            </div>
            <div style={{ fontSize: '12px', color: '#8c8c8c' }}>
              {validation.missingRequirements.includes('Ad Group')
                ? 'No Ad Group'
                : validation.missingRequirements.includes('Creatives')
                  ? 'No Creatives'
                  : `${creativeCount} Creatives`}
            </div>
          </div>
        );
      },
    },
  ];

  const renderLineItemsDetails = (record) => {
    const lineItems = getAllLineItems(record);

    if (lineItems.length === 0) {
      return (
        <div style={{ padding: '16px', textAlign: 'center', backgroundColor: '#fafafa' }}>
          <CloseCircleOutlined
            style={{ color: '#ff4d4f', fontSize: '24px', marginBottom: '8px' }}
          />
          <Text type="secondary">No line items found for this campaign order</Text>
        </div>
      );
    }

    // Add campaign order record reference to each line item for validation
    const lineItemsWithRecord = lineItems.map((lineItem) => ({
      ...lineItem,
      geoTargets: record.geoTargets,
    }));

    return (
      <div style={{ padding: '16px', backgroundColor: '#fafafa', margin: '8px 0' }}>
        <div style={{ display: 'flex', gap: '16px', marginBottom: '16px' }}>
          {record.notes && record.notes.trim().length > 0 && (
            <div
              style={{
                flex: 1,
                padding: '12px',
                backgroundColor: 'white',
                borderRadius: '6px',
                border: '1px solid #e8e8e8',
              }}
            >
              <Text
                strong
                style={{
                  fontSize: '14px',
                  color: '#1890ff',
                  display: 'block',
                  marginBottom: '8px',
                }}
              >
                <FileTextOutlined style={{ marginRight: '6px' }} />
                Campaign Notes:
              </Text>
              <Text
                style={{
                  fontSize: '13px',
                  color: '#333',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                  lineHeight: '1.5',
                }}
              >
                {record.notes}
              </Text>
            </div>
          )}

          {(() => {
            const collaborators = getCollaborators(record);
            if (collaborators.length > 0) {
              return (
                <div
                  style={{
                    flex: 1,
                    padding: '12px',
                    backgroundColor: 'white',
                    borderRadius: '6px',
                    border: '1px solid #e8e8e8',
                  }}
                >
                  <Text
                    strong
                    style={{
                      fontSize: '14px',
                      color: '#52c41a',
                      display: 'block',
                      marginBottom: '8px',
                    }}
                  >
                    <TeamOutlined style={{ marginRight: '6px' }} />
                    Campaign Collaborators:
                  </Text>
                  <div style={{ fontSize: '13px', color: '#333', lineHeight: '1.5' }}>
                    {collaborators.map((collaborator, index) => (
                      <div key={collaborator.id} style={{ marginBottom: '4px' }}>
                        <Text
                          strong
                          style={{ color: collaborator.role === 'Creator' ? '#1890ff' : '#52c41a' }}
                        >
                          {collaborator.role}:
                        </Text>{' '}
                        {collaborator.name}
                        <Text type="secondary" style={{ fontSize: '11px', marginLeft: '8px' }}>
                          {moment(collaborator.lastActivity).format('MM/DD/YYYY')}
                        </Text>
                      </div>
                    ))}
                  </div>
                </div>
              );
            }
            return null;
          })()}
        </div>

        <div style={{ marginBottom: '12px' }}>
          <Text strong style={{ fontSize: '14px' }}>
            Line Items Details ({lineItems.length} items)
          </Text>
        </div>
        <Table
          columns={lineItemColumns}
          dataSource={lineItemsWithRecord}
          rowKey={(lineItem, index) => `${lineItem.id}-${index}`}
          pagination={false}
          size="middle"
          showHeader={true}
          style={{ backgroundColor: 'white' }}
        />
      </div>
    );
  };

  return (
    <Card
      title="DRAFT CAMPAIGNS"
      extra={
        <Space size="middle">
          <Space size="small">
            <Switch
              checked={filterMediaPlanStrategy}
              onChange={handleMediaPlanStrategyFilterChange}
              size="small"
            />
            <span style={{ fontSize: '12px' }}>Has Media Plan Strategy</span>
          </Space>
          <CampaignOrderQueueFilters
            handleFilterChanged={handleFilterChanged}
            setFilteredOrgId={setFilteredOrgId}
            filteredOrgId={filteredOrgId}
          />
        </Space>
      }
    >
      <Table
        columns={columns}
        dataSource={campaignOrders}
        rowKey="id"
        loading={loadingCampaignOrdersDraft}
        pagination={{
          current: pagination.current,
          pageSize: pagination.pageSize,
          total: pagination.total,
          showSizeChanger: true,
        }}
        expandable={{
          expandedRowKeys,
          onExpandedRowsChange: setExpandedRowKeys,
          expandedRowRender: renderLineItemsDetails,
          rowExpandable: (record) => getAllLineItems(record).length > 0,
          expandIcon: ({ expanded, onExpand, record }) =>
            expanded ? (
              <MinusOutlined
                onClick={(e) => onExpand(record, e)}
                style={{ cursor: 'pointer', fontSize: '12px' }}
              />
            ) : (
              <PlusOutlined
                onClick={(e) => onExpand(record, e)}
                style={{ cursor: 'pointer', fontSize: '12px' }}
              />
            ),
        }}
        onChange={handleTableChange}
        scroll={{ y: 600 }}
      />
    </Card>
  );
};
